import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'

interface MarkdownRendererProps {
  content?: string | null
  compact?: boolean
  className?: string
  style?: CSSProperties
}

const isSafeUrl = (url?: string | null) => {
  if (!url) return false
  const trimmed = url.trim()
  const lower = trimmed.toLowerCase()
  return (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('#')
  )
}

export default function MarkdownRenderer({ content, compact = false, className, style }: MarkdownRendererProps) {
  const { t } = useTranslation()
  const markdown = (content || '').trim()

  const components: Components = {
    a: ({ href, children, ...props }) => {
      const safeHref = isSafeUrl(href) ? href : undefined
      const isExternal = Boolean(safeHref && /^https?:\/\//i.test(safeHref))

      return (
        <a
          {...props}
          href={safeHref}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noreferrer noopener' : undefined}
        >
          {children}
        </a>
      )
    },
    img: ({ src, alt, ...props }) => {
      const safeSrc = isSafeUrl(src) ? src : undefined
      if (!safeSrc) return null

      return <img {...props} src={safeSrc} alt={alt || t('markdown.imageAlt')} loading="lazy" />
    },
    code: ({ className: codeClassName, children, ...props }) => {
      const languageMatch = /language-([\w-]+)/.exec(codeClassName || '')
      const text = String(children).replace(/\n$/, '')
      const isBlock = Boolean(languageMatch) || text.includes('\n')

      if (!isBlock) {
        return (
          <code
            {...props}
            className={`rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] text-foreground ${codeClassName ?? ''}`}
          >
            {children}
          </code>
        )
      }

      return (
        <pre className="nf-markdown-code">
          {languageMatch ? <div className="nf-markdown-code-lang">{languageMatch[1]}</div> : null}
          <code {...props} className={codeClassName}>
            {text}
          </code>
        </pre>
      )
    },
    table: ({ children, ...props }) => (
      <div className="nf-markdown-table-wrap">
        <table {...props}>{children}</table>
      </div>
    ),
  }

  return (
    <div className={`nf-markdown ${compact ? 'nf-markdown-compact' : ''} ${className ?? ''}`} style={style}>
      <style>
        {`
          .nf-markdown {
            color: var(--foreground);
            line-height: 1.75;
            word-break: break-word;
          }
          .nf-markdown > :first-child {
            margin-top: 0 !important;
          }
          .nf-markdown > :last-child {
            margin-bottom: 0 !important;
          }
          .nf-markdown p {
            margin: 0 0 ${compact ? 8 : 12}px;
            white-space: pre-wrap;
          }
          .nf-markdown h1,
          .nf-markdown h2,
          .nf-markdown h3,
          .nf-markdown h4,
          .nf-markdown h5,
          .nf-markdown h6 {
            margin: ${compact ? 12 : 18}px 0 ${compact ? 6 : 10}px;
            color: var(--foreground);
            font-weight: 600;
            line-height: 1.35;
          }
          .nf-markdown h1 {
            font-size: ${compact ? 20 : 26}px;
            padding-bottom: 0.3em;
            border-bottom: 1px solid var(--border);
          }
          .nf-markdown h2 {
            font-size: ${compact ? 18 : 22}px;
            padding-bottom: 0.25em;
            border-bottom: 1px solid var(--border);
          }
          .nf-markdown h3 {
            font-size: ${compact ? 16 : 18}px;
          }
          .nf-markdown h4,
          .nf-markdown h5,
          .nf-markdown h6 {
            font-size: ${compact ? 14 : 16}px;
          }
          .nf-markdown ul,
          .nf-markdown ol {
            padding-left: 1.7em;
            margin: 0 0 ${compact ? 8 : 12}px;
          }
          .nf-markdown li {
            margin-bottom: 4px;
          }
          .nf-markdown li > p {
            margin-bottom: 4px;
          }
          .nf-markdown input[type='checkbox'] {
            margin-right: 6px;
          }
          .nf-markdown blockquote {
            margin: 0 0 ${compact ? 8 : 12}px;
            padding: 8px 12px;
            border-left: 4px solid var(--primary);
            background: var(--muted);
            border-radius: 8px;
            color: var(--muted-foreground);
          }
          .nf-markdown blockquote > :last-child {
            margin-bottom: 0;
          }
          .nf-markdown-code {
            position: relative;
            margin: 0 0 ${compact ? 8 : 12}px;
            padding: ${compact ? '10px 12px' : '14px 16px'};
            overflow: auto;
            border-radius: 8px;
            background: var(--muted);
            border: 1px solid var(--border);
          }
          .nf-markdown pre .nf-markdown-code {
            margin: 0;
          }
          .nf-markdown-code code {
            font-family: Consolas, Monaco, 'Courier New', monospace;
            font-size: 13px;
            white-space: pre;
          }
          .nf-markdown-code-lang {
            margin-bottom: 8px;
            color: var(--muted-foreground);
            font-size: 12px;
          }
          .nf-markdown :not(pre) > code {
            font-family: Consolas, Monaco, 'Courier New', monospace;
          }
          .nf-markdown a {
            color: var(--primary);
          }
          .nf-markdown img {
            display: block;
            max-width: 100%;
            max-height: ${compact ? 260 : 420}px;
            margin: 8px 0 ${compact ? 8 : 12}px;
            border-radius: 8px;
            border: 1px solid var(--border);
            object-fit: contain;
          }
          .nf-markdown hr {
            margin: ${compact ? 12 : 18}px 0;
            border: none;
            border-top: 1px solid var(--border);
          }
          .nf-markdown del {
            color: var(--muted-foreground);
          }
          .nf-markdown-table-wrap {
            width: 100%;
            margin: 0 0 ${compact ? 8 : 12}px;
            overflow-x: auto;
          }
          .nf-markdown table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;
          }
          .nf-markdown th,
          .nf-markdown td {
            padding: 8px 10px;
            border: 1px solid var(--border);
            text-align: left;
            vertical-align: top;
          }
          .nf-markdown th {
            background: var(--muted);
            color: var(--foreground);
            font-weight: 600;
          }
          .nf-markdown tr:nth-child(even) td {
            background: color-mix(in oklch, var(--muted) 55%, transparent);
          }
        `}
      </style>
      {markdown ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]} components={components}>
          {markdown}
        </ReactMarkdown>
      ) : (
        <p className="text-sm text-muted-foreground">{t('markdown.empty')}</p>
      )}
    </div>
  )
}
